"use client";

import { useState, useEffect } from "react";
import type { CSSProperties } from "react";
import { useScrollReveal } from "@/shared/lib/useScrollReveal";
import { TIMEZONES } from "@/shared/config";

interface FormData {
  name: string;
  phone: string;
  preferredTime: string;
  timezone: string;
  contactMethod: string;
  privacyAccepted: boolean;
}

interface FormErrors {
  name?: string;
  phone?: string;
  contactMethod?: string;
  privacyAccepted?: string;
}

export function ContactForm({ preselectedSpecialist }: { preselectedSpecialist?: string } = {}) {
  const { ref: formRevealRef, state: formState } = useScrollReveal("contact-form", 0.2);
  const [specialistName, setSpecialistName] = useState(preselectedSpecialist ?? "");

  useEffect(() => {
    if (preselectedSpecialist) setSpecialistName(preselectedSpecialist);
  }, [preselectedSpecialist]);

  /** Staggered field entry — SSR safe (stays visible until hook resolves) */
  const field = (delay: number): CSSProperties => {
    if (formState === "done") return {};
    if (formState === "idle") return { opacity: 0, transform: "translateY(1.5rem)" };
    return {
      opacity: 0,
      animation: `slide-from-bottom 0.55s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${delay.toFixed(2)}s forwards`,
    };
  };

  const [formData, setFormData] = useState<FormData>({
    name: "",
    phone: "+7",
    preferredTime: "any",
    timezone: "Europe/Moscow",
    contactMethod: "phone",
    privacyAccepted: false,
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [timezoneDetected, setTimezoneDetected] = useState(false);

  useEffect(() => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const isKnown = TIMEZONES.some((t) => t.value === tz);
      setFormData((prev) => ({
        ...prev,
        timezone: isKnown ? tz : "Europe/Moscow",
      }));
      setTimezoneDetected(isKnown);
    } catch {
      setTimezoneDetected(false);
    }
  }, []);

  const formatPhone = (value: string) => {
    const phone = value.replace(/\D/g, "");
    if (phone.length <= 1) return "+7 (";
    let formatted = "+7 (";
    if (phone.length > 1) formatted += phone.substring(1, 4);
    if (phone.length >= 5) formatted += `) ${phone.substring(4, 7)}`;
    if (phone.length >= 8) formatted += `-${phone.substring(7, 9)}`;
    if (phone.length >= 10) formatted += `-${phone.substring(9, 11)}`;
    return formatted;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    if (errors.phone) setErrors({ ...errors, phone: undefined });
    if (value === "+7 ") {
      setFormData({ ...formData, phone: "+7" });
      return;
    }
    if (!value.startsWith("+7")) value = "+7";
    setFormData({ ...formData, phone: formatPhone(value) });
  };

  const getPhoneMaskElements = (value: string) => {
    const phone = value.replace(/\D/g, "");
    if (phone.length === 0)
      return <span className="text-foreground/30">+7 (___) ___-__-__</span>;
    let fullMask = value;
    const maskTemplate = "+7 (___) ___-__-__";
    if (fullMask.length < maskTemplate.length)
      fullMask += maskTemplate.substring(fullMask.length);
    return (
      <>
        <span className="opacity-0">{fullMask.substring(0, value.length)}</span>
        <span className="text-foreground/30">
          {fullMask.substring(value.length)}
        </span>
      </>
    );
  };

  const handlePhoneFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (formData.phone === "+7" || formData.phone === "+7 ") {
      setFormData({ ...formData, phone: "+7 (" });
      setTimeout(() => e.target.setSelectionRange(4, 4), 0);
    } else if (formData.phone === "+7 (") {
      setTimeout(() => e.target.setSelectionRange(4, 4), 0);
    }
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.name.trim())
      newErrors.name = "Напишите, пожалуйста, как к вам обращаться";
    if (formData.phone.replace(/\D/g, "").length < 11)
      newErrors.phone = "Кажется, в номере не хватает цифр";
    if (!formData.privacyAccepted)
      newErrors.privacyAccepted = "Нужно ваше согласие на обработку данных";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNameBlur = () => {
    if (!formData.name.trim())
      setErrors((p) => ({
        ...p,
        name: "Напишите, пожалуйста, как к вам обращаться",
      }));
  };

  const handlePhoneBlur = () => {
    const d = formData.phone.replace(/\D/g, "");
    if (d.length > 1 && d.length < 11)
      setErrors((p) => ({ ...p, phone: "Кажется, в номере не хватает цифр" }));
    else if (d.length <= 1)
      setErrors((p) => ({
        ...p,
        phone: "Напишите, пожалуйста, номер, чтобы мы могли связаться",
      }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    await new Promise((r) => setTimeout(r, 1000));
    setIsSubmitting(false);
    setIsSuccess(true);
  };

  // Button is active only when all required fields are filled
  const isFormValid =
    formData.name.trim() !== "" &&
    formData.phone.replace(/\D/g, "").length >= 11 &&
    formData.privacyAccepted;

  if (isSuccess) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center gap-4 p-4">
        <div className="w-16 h-16 rounded-full bg-help/10 flex items-center justify-center text-help text-3xl mb-2">
          ✓
        </div>
        <h3 className="font-heading text-fluid-xl text-foreground">
          Заявка отправлена 🎉
        </h3>
        <p className="font-content text-foreground/80">
          Спасибо! Мы свяжемся с вами в ближайшее время.
        </p>
        <button
          onClick={() => {
            setIsSuccess(false);
            setFormData({
              name: "",
              phone: "+7",
              preferredTime: "any",
              timezone: "Europe/Moscow",
              contactMethod: "phone",
              privacyAccepted: false,
            });
          }}
          className="btn-solid mt-fluid-md"
        >
          Отправить еще
        </button>
      </div>
    );
  }

  const FIELD_BASE =
    "w-full bg-transparent border-0 border-b pb-2 text-foreground text-fluid-base placeholder:text-foreground/30 focus:outline-none transition-colors duration-300";
  const FIELD_NORMAL = "border-border/40 focus:border-help";
  const CHEVRON = (
    <div className="pointer-events-none absolute bottom-2 right-0 text-foreground/40">
      <svg
        className="w-4 h-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M19 9l-7 7-7-7"
        />
      </svg>
    </div>
  );

  return (
    <form
      ref={(el) => formRevealRef(el as HTMLElement | null)}
      onSubmit={handleSubmit}
      className="flex flex-col w-full"
    >
      <div className="flex flex-col gap-8">
        {/* Specialist pre-selection badge */}
        {specialistName && (
          <div
            style={field(0)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-[var(--help)]/30 bg-[var(--help)]/5"
          >
            <span className="font-content text-xs uppercase tracking-widest text-[var(--help)]">
              Специалист
            </span>
            <span className="font-content text-sm text-foreground font-medium flex-1 truncate">
              {specialistName}
            </span>
            <button
              type="button"
              onClick={() => setSpecialistName("")}
              className="text-foreground/30 hover:text-foreground/60 transition-colors text-xs leading-none"
            >
              ✕
            </button>
          </div>
        )}
        <div style={field(0.1)} className="flex flex-col gap-1">
          <label
            htmlFor="cf-name"
            className="text-xs uppercase tracking-widest text-foreground/40 font-content"
          >
            Как к вам обращаться{" "}
            <span className="text-[var(--media,orange)]">*</span>
          </label>
          <input
            type="text"
            id="cf-name"
            name="name"
            autoComplete="name"
            value={formData.name}
            onChange={(e) => {
              setFormData({ ...formData, name: e.target.value });
              if (errors.name) setErrors({ ...errors, name: undefined });
            }}
            onBlur={handleNameBlur}
            placeholder="Ваше имя"
            className={`${FIELD_BASE} ${errors.name ? "border-[var(--media,orange)]" : FIELD_NORMAL}`}
          />
          {errors.name && (
            <span className="text-xs text-[var(--destructive)] mt-1">
              {errors.name}
            </span>
          )}
        </div>

        <div style={field(0.22)} className="flex flex-col gap-1">
          <label
            htmlFor="cf-phone"
            className="text-xs uppercase tracking-widest text-foreground/40 font-content"
          >
            Телефон <span className="text-[var(--media,orange)]">*</span>
          </label>
          <div className="relative">
            <input
              type="tel"
              id="cf-phone"
              name="tel"
              autoComplete="tel"
              value={formData.phone}
              onChange={handlePhoneChange}
              onFocus={handlePhoneFocus}
              onBlur={handlePhoneBlur}
              onClick={(e) => {
                const t = e.target as HTMLInputElement;
                if (t.selectionStart !== null && t.selectionStart < 4)
                  t.setSelectionRange(4, 4);
              }}
              onKeyDown={(e) => {
                const t = e.target as HTMLInputElement;
                if (
                  (e.key === "Backspace" || e.key === "ArrowLeft") &&
                  t.selectionStart !== null &&
                  t.selectionStart <= 4
                )
                  e.preventDefault();
              }}
              maxLength={18}
              className={`${FIELD_BASE} relative z-10 ${errors.phone ? "border-[var(--media,orange)]" : FIELD_NORMAL}`}
            />
            <span className="absolute bottom-2 left-0 text-foreground/30 pointer-events-none z-0 text-fluid-base flex whitespace-pre">
              {getPhoneMaskElements(formData.phone)}
            </span>
          </div>
          {errors.phone && (
            <span className="text-xs text-[var(--destructive)] mt-1">
              {errors.phone}
            </span>
          )}
        </div>

        <div style={field(0.34)} className="flex flex-col gap-1">
          <label
            htmlFor="cf-contactMethod"
            className="text-xs uppercase tracking-widest text-foreground/40 font-content"
          >
            Как лучше связаться
          </label>
          <div className="relative">
            <select
              id="cf-contactMethod"
              value={formData.contactMethod}
              onChange={(e) =>
                setFormData({ ...formData, contactMethod: e.target.value })
              }
              className={`${FIELD_BASE} ${FIELD_NORMAL} appearance-none pr-6`}
            >
              <option value="phone" className="bg-background">
                Перезвоните мне
              </option>
              <option value="telegram" className="bg-background">
                Телеграм
              </option>
              <option value="whatsapp" className="bg-background">
                WhatsApp
              </option>
              <option value="max" className="bg-background">
                Мессенджер MAX
              </option>
            </select>
            {CHEVRON}
          </div>
        </div>

        <div style={field(0.46)} className="flex flex-col gap-1">
          <label
            htmlFor="cf-preferredTime"
            className="text-xs uppercase tracking-widest text-foreground/40 font-content"
          >
            Во сколько можно позвонить
          </label>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
            <div className="relative flex-1">
              <select
                id="cf-preferredTime"
                value={formData.preferredTime}
                onChange={(e) =>
                  setFormData({ ...formData, preferredTime: e.target.value })
                }
                className={`${FIELD_BASE} ${FIELD_NORMAL} appearance-none pr-6`}
              >
                <option value="any" className="bg-background">
                  В любое время
                </option>
                <option value="morning" className="bg-background">
                  Утром (9:00 — 12:00)
                </option>
                <option value="afternoon" className="bg-background">
                  Днем (12:00 — 18:00)
                </option>
                <option value="evening" className="bg-background">
                  Вечером (18:00 — 21:00)
                </option>
              </select>
              {CHEVRON}
            </div>
            <div className="relative sm:w-2/5">
              <select
                id="cf-timezone"
                value={formData.timezone}
                onChange={(e) =>
                  setFormData({ ...formData, timezone: e.target.value })
                }
                className={`${FIELD_BASE} ${FIELD_NORMAL} appearance-none pr-6`}
              >
                {TIMEZONES.map((tz) => (
                  <option
                    key={tz.value}
                    value={tz.value}
                    className="bg-background"
                  >
                    {tz.label}
                  </option>
                ))}
              </select>
              {CHEVRON}
            </div>
          </div>
          {!timezoneDetected && (
            <span className="text-xs text-foreground/40 mt-1">
              Часовой пояс не определён — по умолчанию Москва
            </span>
          )}
        </div>

        <div style={field(0.58)} className="flex flex-col gap-1 mt-2">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="cf-privacy"
              checked={formData.privacyAccepted}
              onChange={(e) => {
                setFormData({ ...formData, privacyAccepted: e.target.checked });
                if (errors.privacyAccepted)
                  setErrors({ ...errors, privacyAccepted: undefined });
              }}
              className="w-4 h-4 rounded border border-foreground/30 text-help focus:ring-help/30 shrink-0 cursor-pointer transition-all duration-300"
            />
            <label
              htmlFor="cf-privacy"
              className="text-xs text-foreground/60 leading-relaxed cursor-pointer"
            >
              Согласен с{" "}
              <a
                href="/privacy"
                className="text-help hover:underline"
                target="_blank"
              >
                Политикой конфиденциальности
              </a>{" "}
              (ФЗ №152-ФЗ)
            </label>
          </div>
          {errors.privacyAccepted && (
            <span className="text-xs text-[var(--destructive)] mt-1 pl-7">
              {errors.privacyAccepted}
            </span>
          )}
        </div>
      </div>

      <div id="cf-captcha-container" className="hidden" />

      <div className="mt-auto pt-fluid-xl">
        <div className="relative group/submit w-full">
          <button
            type="submit"
            disabled={isSubmitting || !isFormValid}
            className="btn-light text-[var(--help)] w-full lg:hover:bg-[var(--help)] lg:hover:!text-white disabled:opacity-40 disabled:pointer-events-none"
          >
            {isSubmitting ? "Отправка..." : "Оставить заявку"}
          </button>
          {!isFormValid && !isSubmitting && (
            <div className="absolute -top-9 left-1/2 -translate-x-1/2 bg-foreground/90 text-white text-xs px-3 py-1.5 rounded-lg whitespace-nowrap opacity-0 group-hover/submit:opacity-100 transition-opacity duration-150 pointer-events-none select-none z-10">
              Пожалуйста, заполните форму
            </div>
          )}
        </div>
      </div>
    </form>
  );
}
